/**
 * Application router using @lit-labs/router.
 * Includes auth guard for protected routes.
 */
import { Router } from '@lit-labs/router';
import {
  authState,
  canAccessAppAdminConfiguration,
  canAccessSecretaryFeatures,
  canManageTrialEvents,
} from './state/auth.state';
import {
  clearReturnUrl,
  getAuthCallbackPath,
  getCurrentAppPath,
  getReturnUrl,
  isAuthCallbackPath,
  setReturnUrl,
} from './auth-navigation';

/**
 * Route definition type for our application.
 */
interface RouteDefinition {
  path: string;
  enter?: () => Promise<boolean> | boolean;
  render?: (params: Record<string, string>) => Element;
}

interface NavigateOptions {
  replace?: boolean;
}

export const navigationAdapter = {
  redirectToDesignSystem(): void {
    window.location.assign('/design-system.html');
  },
};

/**
 * Route configuration.
 */
const routeDefinitions: RouteDefinition[] = [
  {
    path: '/',
    enter: () => {
      // Home redirects to trials page
      window.history.replaceState(null, '', '/trials');
      return false; // Prevent rendering, will re-route
    },
  },
  {
    path: '/trials',
    render: () => {
      const container = document.createElement('div');
      container.innerHTML = '<dt-trials-page></dt-trials-page>';
      return container.firstElementChild!;
    },
  },
  {
    path: '/trials/:id/register',
    enter: () => guardProtectedRoute(),
    render: (params) => {
      const page = document.createElement('dt-registration-page');
      page.setAttribute('trial-event-id', params.id || '');
      return page;
    },
  },
  {
    path: '/trial-events/:trialId/confirmation/:entryId',
    render: (params) => {
      const page = document.createElement('dt-confirmation-page');
      page.setAttribute('entry-id', params.entryId || '');
      page.setAttribute('trial-event-id', params.trialId || '');
      return page;
    },
  },
  {
    path: '/secretary',
    enter: () => guardTrialManagementRoute(),
    render: () => {
      const container = document.createElement('div');
      container.innerHTML = '<dt-secretary-page></dt-secretary-page>';
      return container.firstElementChild!;
    },
  },
  {
    path: '/secretary/trial-events/new',
    enter: () => guardTrialManagementRoute(),
    render: () => {
      const page = document.createElement('dt-trial-event-detail-page');
      page.setAttribute('create', '');
      return page;
    },
  },
  {
    path: '/secretary/entries/:trialId/:entryId',
    enter: () => guardSecretaryRoute(),
    render: (params) => {
      const page = document.createElement('dt-secretary-detail-page');
      page.setAttribute('entry-id', params.entryId || '');
      page.setAttribute('trial-event-id', params.trialId || '');
      return page;
    },
  },
  {
    path: '/login',
    render: () => {
      const container = document.createElement('div');
      container.innerHTML = '<dt-login-page></dt-login-page>';
      return container.firstElementChild!;
    },
  },
  {
    path: '/design-system',
    enter: () => {
      navigationAdapter.redirectToDesignSystem();
      return false;
    },
  },
  {
    path: getAuthCallbackPath(),
    render: () => {
      const container = document.createElement('div');
      container.innerHTML = '<dt-auth-callback-page></dt-auth-callback-page>';
      return container.firstElementChild!;
    },
  },
  {
    path: '/forbidden',
    render: () => {
      const container = document.createElement('div');
      container.innerHTML = '<dt-forbidden-page></dt-forbidden-page>';
      return container.firstElementChild!;
    },
  },
  {
    path: '/error',
    render: () => {
      const container = document.createElement('div');
      container.innerHTML = '<dt-error-page></dt-error-page>';
      return container.firstElementChild!;
    },
  },
  {
    path: '/admin/trial-events/:trialEventId',
    enter: () => guardTrialManagementRoute(),
    render: (params) => {
      const page = document.createElement('dt-trial-event-detail-page');
      page.setAttribute('trial-event-id', params.trialEventId || '');
      return page;
    },
  },
  {
    path: '/admin/configuration',
    enter: () => guardAppAdminConfigurationRoute(),
    render: () => {
      const page = document.createElement('dt-admin-organizations-page');
      return page;
    },
  },
  {
    path: '/admin/configuration/organizations',
    enter: () => guardAppAdminConfigurationRoute(),
    render: () => {
      const page = document.createElement('dt-admin-organizations-page');
      return page;
    },
  },
  {
    path: '/admin/configuration/sport-codes',
    enter: () => guardAppAdminConfigurationRoute(),
    render: () => {
      const page = document.createElement('dt-admin-sport-codes-page');
      return page;
    },
  },
  {
    path: '/admin/configuration/organizations/:organizationId/sport-codes',
    enter: () => guardAppAdminConfigurationRoute(),
    render: (params) => {
      const page = document.createElement('dt-admin-organization-sport-codes-page');
      page.setAttribute('organization-id', params.organizationId || '');
      return page;
    },
  },
  {
    path: '/admin/configuration/form-templates',
    enter: () => guardAppAdminConfigurationRoute(),
    render: () => {
      const page = document.createElement('dt-admin-form-templates-page');
      return page;
    },
  },
  {
    // Fallback 404
    path: '/*',
    render: () => {
      const container = document.createElement('div');
      container.innerHTML = '<dt-error-page code="404"></dt-error-page>';
      return container.firstElementChild!;
    },
  },
];

/**
 * Guard function for any protected route (requires authentication only).
 * Returns true if user can access, false to redirect.
 */
export function guardProtectedRoute(): boolean {
  const auth = authState.get();

  if (!auth.isAuthenticated) {
    // Save return URL for post-login redirect
    setReturnUrl(getCurrentAppPath());
    // Redirect to login
    navigate('/login', { replace: true });
    return false;
  }

  return true;
}

/**
 * Guard function for secretary routes.
 * Returns true if user can access, false to redirect.
 */
function guardSecretaryRoute(): boolean {
  const auth = authState.get();

  if (!auth.isAuthenticated) {
    // Save return URL for post-login redirect
    setReturnUrl(getCurrentAppPath());
    // Redirect to login
    navigate('/login', { replace: true });
    return false;
  }

  if (!canAccessSecretaryFeatures.get()) {
    // User is authenticated but doesn't have permission
    navigate('/forbidden', { replace: true });
    return false;
  }

  return true;
}

function guardTrialManagementRoute(): boolean {
  const auth = authState.get();

  if (!auth.isAuthenticated) {
    setReturnUrl(getCurrentAppPath());
    navigate('/login', { replace: true });
    return false;
  }

  if (!canManageTrialEvents.get()) {
    navigate('/forbidden', { replace: true });
    return false;
  }

  return true;
}

function guardAppAdminConfigurationRoute(): boolean {
  const auth = authState.get();

  if (!auth.isAuthenticated) {
    setReturnUrl(getCurrentAppPath());
    navigate('/login', { replace: true });
    return false;
  }

  if (!canAccessAppAdminConfiguration.get()) {
    navigate('/forbidden', { replace: true });
    return false;
  }

  return true;
}

// Note: @lit-labs/router requires a ReactiveControllerHost. For now, we export
// the route definitions and create a simple hash-based router for the MVP.
// The full @lit-labs/router integration will be completed in E00c (App Shell).

/**
 * Simple hash router for MVP.
 * Will be replaced with @lit-labs/router when used within a Lit element.
 */
export function initializeRouter(outlet: HTMLElement): void {
  const handleRoute = () => {
    const path = window.location.pathname;
    
    for (const route of routeDefinitions) {
      const pattern = route.path.replace(/:(\w+)/g, '(?<$1>[^/]+)').replace('/*', '.*');
      const regex = new RegExp(`^${pattern}$`);
      const match = path.match(regex);
      
      if (match) {
        // Check enter guard
        if (route.enter) {
          const canEnter = route.enter();
          if (canEnter instanceof Promise) {
            canEnter.then((allowed) => {
              if (allowed && route.render) {
                outlet.innerHTML = '';
                outlet.appendChild(route.render(match.groups || {}));
              }
            });
            return;
          } else if (!canEnter) {
            return;
          }
        }
        
        // Render the route
        if (route.render) {
          outlet.innerHTML = '';
          outlet.appendChild(route.render(match.groups || {}));
        }
        return;
      }
    }
  };
  
  // Handle initial route
  handleRoute();
  
  // Handle navigation
  window.addEventListener('popstate', handleRoute);
}

/**
 * Navigate to a new path programmatically.
 */
export function navigate(path: string, options: NavigateOptions = {}): void {
  if (options.replace) {
    window.history.replaceState(null, '', path);
  } else {
    window.history.pushState(null, '', path);
  }

  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Export for documentation/testing
export { routeDefinitions, Router };
export { clearReturnUrl, getAuthCallbackPath, getReturnUrl, isAuthCallbackPath, setReturnUrl };
export type { RouteDefinition };
